// Basic
import { useState } from 'react';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
// Libs
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { cn } from '@/common-utils/libraries/tailwind/utils';
// Store & actions
import actionsLogin from '../../api/actionsLogin';
// Components
import ICONS from '../../../../../common-utils/enums/icons';
import { Button } from '../../../../../common-utils/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormMessage } from '../../../../../common-utils/components/ui/form';
import { Input } from '../../../../../common-utils/components/ui/input';
import Spinner from '@/common-utils/components/renders/spinner/Spinner';
import Icon from '@/common-utils/components/renders/icon/Icon';
import Entity from '@/common-utils/components/renders/entity/Entity';
// Common
import LOGIN_SCREENS from '@/app/modules/login/enums/LoginScreens';
// Css
import './ForgotPassword.scss';
// Lang
import { nsLogin } from '@/app/i18n/namespaces';

/**
 * @param {Function} exitScreen function to switch for sibling screen
 * @returns {JSX.Element} ForgotPassword component
*/
function ForgotPassword({ exitScreen }) {
    // Constants
    const formSchema = z.object({
        email: z.string().email()
    });

    // State
    const [successReset, setSuccessReset] = useState(false);
    const [submitError, setSubmitError] = useState(null);

    // API Call
    const onSuccess = () => {
        setSuccessReset(true);
    };
    const onError = (error) => {
        const errorCode = error.response?.data?.errorCode ?? error.code ?? 'UNKNOWN_ERROR';
        setSubmitError(errorCode);
    };

    const submitForgotPassword = actionsLogin.forgottenPassword(onSuccess, onError);

    // Form definition
    const form = useForm({
        resolver: zodResolver(formSchema),
        defaultValues: {
            email: ''
        }
    });

    const onSubmit = (values) => {
        if (Object.keys(form.formState.errors).length === 0) {
            submitForgotPassword.mutate(values);
        }
    };

    return (
        <div>
            {
                successReset ?
                    <div className='forgot-pwd-success'>
                        <Entity namespace={nsLogin} entity='forgotten_Pwd.success' className='success-message' />
                        <p>
                            {form.getValues().email}
                        </p>
                        <div className='flex justify-center'>
                            <Button
                                variant='link'
                                namespace={nsLogin}
                                entity='back_to_login'
                                icon={ICONS.ARROW_LEFT}
                                onClick={() => exitScreen(LOGIN_SCREENS.FORGOT_PASSWORD)}
                            />
                        </div>
                    </div>
                    :
                    <div>
                        <div className='forgot-pwd-description'>
                            <Entity namespace={nsLogin} entity='forgotten_Pwd.title' />
                            <Entity namespace={nsLogin} entity='forgotten_Pwd.description_1' className='forgot-pwd-text' />
                            <Entity namespace={nsLogin} entity='forgotten_Pwd.description_2' className='forgot-pwd-text' />
                            <div className='flex justify-center'>
                                <Icon className='text-warning mr-1' icon={ICONS.WARNING} />
                                <Entity namespace={nsLogin} entity='forgotten_Pwd.description_warning' className='forgot-pwd-text' />
                            </div>
                        </div>
                        <Form {...form}>
                            <form className='login-form space-y-4 mt-4' onSubmit={form.handleSubmit(onSubmit)}>
                                <FormField
                                    control={form.control}
                                    name='email'
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormControl>
                                                <Input type='email' namespace={nsLogin} entity='email' {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <div className='text-center'>
                                    <Button
                                        variant='primary'
                                        type='submit'
                                        namespace={nsLogin}
                                        entity='send_reset_mail'
                                        icon={ICONS.SEND}
                                        size='lg'
                                        disabled={submitForgotPassword.isLoading}
                                    />
                                </div>
                            </form>
                        </Form>
                        <div className={cn('mt-4', 'back-to-login')}>
                            <Button
                                variant='link'
                                namespace={nsLogin}
                                entity='back_to_login'
                                icon={ICONS.ARROW_LEFT}
                                onClick={() => exitScreen(LOGIN_SCREENS.FORGOT_PASSWORD)}
                                disabled={submitForgotPassword.isLoading}
                            />
                            {submitForgotPassword.isLoading && <Spinner className={'mt-4'} />}
                        </div>
                        {
                            !submitForgotPassword.isLoading && submitError &&
                            <Entity namespace='errors' entity={`api.${submitError}`} className='form login-error' />
                        }
                    </div >
            }
        </div >
    );
}

ForgotPassword.propTypes = {
    exitScreen: PropTypes.func
};

export default ForgotPassword;